// Firebase/Firestore API service - replaces mock data with real database operations
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  writeBatch,
  increment,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage, functions } from './firebase';

// Properties API
export const firestorePropertiesApi = {
  _toNumberOrNull(v) {
    if (v === undefined || v === null) return null;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/[$,]/g, ''));
    return Number.isFinite(n) ? n : null;
  },

  _createdAtMs(property) {
    const createdAt = property?.createdAt;
    if (!createdAt) return 0;
    if (typeof createdAt.toMillis === 'function') return createdAt.toMillis();
    if (typeof createdAt.seconds === 'number') return createdAt.seconds * 1000;
    const parsed = new Date(createdAt).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  },

  _sortNewest(properties) {
    return [...properties].sort((a, b) => this._createdAtMs(b) - this._createdAtMs(a));
  },

  // Get all properties with optional filtering
  async getAll(filters = {}, pagination = {}) {
    try {
      if (typeof window !== 'undefined') {
        console.log('[Firestore][properties.getAll] start', { filters, pagination });
      }
      let q = collection(db, 'properties');

      const isPublicishVisibility = (v) => v == null || v === 'public' || v === 'Public';
      const queryPublicListings = !filters.visibility;

      // Apply filters
      if (!queryPublicListings && filters.propertyType) {
        q = query(q, where('propertyType', '==', filters.propertyType));
      }
      if (!queryPublicListings && filters.status) {
        q = query(q, where('status', '==', filters.status));
      }
      if (!queryPublicListings && filters.vendorId) {
        q = query(q, where('vendorId', '==', filters.vendorId));
      }

      // Price range filters
      if (!queryPublicListings && filters.minPrice) {
        q = query(q, where('price', '>=', filters.minPrice));
      }
      if (!queryPublicListings && filters.maxPrice) {
        q = query(q, where('price', '<=', filters.maxPrice));
      }

      // Location filter (basic text search)
      if (!queryPublicListings && filters.location) {
        // Note: Firestore doesn't have full-text search, this is a basic implementation
        q = query(q, where('city', '>=', filters.location), where('city', '<=', filters.location + '\uf8ff'));
      }

      if (!filters.visibility) {
        q = query(q, where('visibility', '==', 'public'));
      }

      const querySnapshot = await getDocs(q);
      let properties = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      if (filters.visibility) {
        // If a caller requests a specific visibility, filter client-side to avoid index requirements.
        properties = properties.filter((p) => p.visibility === filters.visibility);
      } else {
        properties = properties.filter((p) => isPublicishVisibility(p.visibility));
      }

      if (queryPublicListings) {
        if (filters.propertyType) {
          properties = properties.filter((p) => p.propertyType === filters.propertyType);
        }
        if (filters.status) {
          properties = properties.filter((p) => p.status === filters.status);
        }
        if (filters.vendorId) {
          properties = properties.filter((p) => p.vendorId === filters.vendorId);
        }
        if (filters.minPrice) {
          properties = properties.filter((p) => {
            const price = this._toNumberOrNull(p.price);
            return price != null && price >= filters.minPrice;
          });
        }
        if (filters.maxPrice) {
          properties = properties.filter((p) => {
            const price = this._toNumberOrNull(p.price);
            return price != null && price <= filters.maxPrice;
          });
        }
        if (filters.location) {
          const location = filters.location.toLowerCase();
          properties = properties.filter((p) => p.city?.toLowerCase().includes(location));
        }
      }

      properties = this._sortNewest(properties);

      if (pagination.limit) {
        properties = properties.slice(0, pagination.limit);
      }

      if (typeof window !== 'undefined') {
        console.log('[Firestore][properties.getAll] done', { total: properties.length });
      }
      return {
        data: properties,
        total: properties.length,
        page: pagination.page || 1,
        limit: pagination.limit || properties.length
      };
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (typeof window !== 'undefined') {
        console.error('[Firestore][properties.getAll] failed', {
          code: error?.code,
          message: error?.message,
          name: error?.name
        });
      }
      throw error;
    }
  },

  // Get single property by ID
  async getById(id) {
    try {
      const docRef = doc(db, 'properties', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Property not found');
      }

      return {
        data: {
          id: docSnap.id,
          ...docSnap.data()
        }
      };
    } catch (error) {
      console.error('Error fetching property:', error);
      throw error;
    }
  },

  // Increment property view counter (public-safe when rules allow views-only updates)
  async incrementViews(propertyId) {
    try {
      const docRef = doc(db, 'properties', propertyId);
      await updateDoc(docRef, { views: increment(1) });
      return { data: { propertyId, incremented: true } };
    } catch (error) {
      console.error('Error incrementing property views:', error);
      throw error;
    }
  },

  // Get properties by vendor
  async getByVendor(vendorId) {
    try {
      const q = query(
        collection(db, 'properties'),
        where('vendorId', '==', vendorId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const properties = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (typeof window !== 'undefined') {
        console.log(`[Firestore] getByVendor(${vendorId}) -> ${properties.length} properties`);
      }

      return {
        data: properties,
        total: properties.length
      };
    } catch (error) {
      console.error('Error fetching vendor properties:', error);
      throw error;
    }
  },

  // Get *public* properties for a vendor (used for public storefront pages)
  async getPublicByVendor(vendorId) {
    try {
      if (typeof window !== 'undefined') {
        console.log('[Firestore][properties.getPublicByVendor] start', { vendorId });
      }
      const isPublicishVisibility = (v) => v == null || v === 'public' || v === 'Public';

      const q = query(
        collection(db, 'properties'),
        where('visibility', '==', 'public')
      );

      const querySnapshot = await getDocs(q);
      const properties = this._sortNewest(querySnapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        .filter((p) => p.vendorId === vendorId && isPublicishVisibility(p.visibility)));

      if (typeof window !== 'undefined') {
        console.log(`[Firestore] getPublicByVendor(${vendorId}) -> ${properties.length} properties`);
      }

      return {
        data: properties,
        total: properties.length
      };
    } catch (error) {
      console.error('Error fetching vendor public properties:', error);
      if (typeof window !== 'undefined') {
        console.error('[Firestore][properties.getPublicByVendor] failed', {
          vendorId,
          code: error?.code,
          message: error?.message,
          name: error?.name
        });
      }
      throw error;
    }
  },

  // Create new property
  async create(propertyData) {
    try {
      const resolvedVendorId = propertyData.vendorId || auth.currentUser?.uid || null;
      const normalizedVisibility = (() => {
        const v = (propertyData.visibility || 'public');
        const normalized = typeof v === 'string' ? v.toLowerCase() : 'public';
        // draft removed -> treat as private for legacy clients/data
        if (normalized === 'draft') return 'private';
        return ['public', 'private'].includes(normalized) ? normalized : 'public';
      })();

      // Normalize price field. Some callers historically send askingPrice; the app
      // UI expects `price` to be a number.
      const normalizedPrice =
        this._toNumberOrNull(propertyData.price) ??
        this._toNumberOrNull(propertyData.askingPrice);

      const newProperty = {
        ...propertyData,
        vendorId: resolvedVendorId,
        visibility: normalizedVisibility,
        ...(normalizedPrice != null ? { price: normalizedPrice } : {}),
        createdAt: new Date(),
        updatedAt: new Date(),
        views: 0,
        favorites: 0
      };

      // Avoid persisting duplicate/legacy field.
      delete newProperty.askingPrice;
      delete newProperty.title;

      const docRef = doc(collection(db, 'properties'));
      await setDoc(docRef, newProperty);

      return {
        data: {
          id: docRef.id,
          ...newProperty
        }
      };
    } catch (error) {
      console.error('Error creating property:', error);
      throw error;
    }
  },

  // Update property
  async update(id, updates) {
    try {
      const docRef = doc(db, 'properties', id);
      const updatedData = {
        ...updates,
        title: deleteField(),
        updatedAt: new Date()
      };

      await updateDoc(docRef, updatedData);

      // Fetch updated document
      const docSnap = await getDoc(docRef);
      return {
        data: {
          id: docSnap.id,
          ...docSnap.data()
        }
      };
    } catch (error) {
      console.error('Error updating property:', error);
      throw error;
    }
  },

  // Delete property
  async delete(id) {
    try {
      const docRef = doc(db, 'properties', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Property not found');
      }

      const propertyData = {
        id: docSnap.id,
        ...docSnap.data()
      };

      await deleteDoc(docRef);

      return {
        data: propertyData
      };
    } catch (error) {
      console.error('Error deleting property:', error);
      throw error;
    }
  },

  // Delete property with cleanup (vendor-only, server-side)
  async deleteCascade(id) {
    try {
      const fn = httpsCallable(functions, 'deleteProperty');
      const result = await fn({ propertyId: id });
      return { data: result.data };
    } catch (error) {
      console.error('Error deleting property (cascade):', error);
      throw error;
    }
  },

  // Send inquiry (store in Firestore)
  async sendInquiry(inquiryData) {
    try {
      const inquiry = {
        ...inquiryData,
        status: 'sent',
        createdAt: new Date()
      };

      const docRef = doc(collection(db, 'inquiries'));
      await setDoc(docRef, inquiry);

      return {
        data: {
          id: docRef.id,
          ...inquiry
        }
      };
    } catch (error) {
      console.error('Error sending inquiry:', error);
      throw error;
    }
  }
  ,

  // -----------------------------
  // Saved Properties (Favorites)
  // Stored at: /properties/{propertyId}/savedBy/{investorId}
  // -----------------------------

  _savedDocRef(propertyId, investorId) {
    return doc(db, 'properties', propertyId, 'savedBy', investorId);
  },

  _userSavedDocRef(investorId, propertyId) {
    return doc(db, 'users', investorId, 'savedProperties', propertyId);
  },

  async isSaved(propertyId, investorId = auth.currentUser?.uid) {
    if (!investorId) return { data: { saved: false } };
    const docRef = this._userSavedDocRef(investorId, propertyId);
    const snap = await getDoc(docRef);
    return { data: { saved: snap.exists() } };
  },

  async save(propertyId, investorMeta = {}) {
    const investorId = auth.currentUser?.uid;
    if (!investorId) {
      throw new Error('Must be signed in to save properties');
    }

    // Fetch property once to capture vendorId (and for rules validation).
    const propertySnap = await getDoc(doc(db, 'properties', propertyId));
    if (!propertySnap.exists()) {
      throw new Error('Property not found');
    }

    const property = propertySnap.data();
    const vendorId = property.vendorId || null;

    const payload = {
      propertyId,
      vendorId,
      investorId,
      investorName: investorMeta.investorName || investorMeta.name || investorMeta.displayName || null,
      createdAt: new Date()
    };

    const batch = writeBatch(db);
    batch.set(this._savedDocRef(propertyId, investorId), payload);
    batch.set(this._userSavedDocRef(investorId, propertyId), payload);
    await batch.commit();
    return { data: payload };
  },

  async unsave(propertyId) {
    const investorId = auth.currentUser?.uid;
    if (!investorId) {
      throw new Error('Must be signed in to unsave properties');
    }
    const batch = writeBatch(db);
    batch.delete(this._savedDocRef(propertyId, investorId));
    batch.delete(this._userSavedDocRef(investorId, propertyId));
    await batch.commit();
    return { data: { propertyId, investorId, deleted: true } };
  },

  async toggleSaved(propertyId, investorMeta = {}) {
    const investorId = auth.currentUser?.uid;
    if (!investorId) {
      throw new Error('Must be signed in to save properties');
    }
    const existing = await getDoc(this._userSavedDocRef(investorId, propertyId));
    if (existing.exists()) {
      await this.unsave(propertyId);
      return { data: { saved: false } };
    }
    await this.save(propertyId, investorMeta);
    return { data: { saved: true } };
  },

  // Investor view: list saved properties
  async getSavedByInvestor(investorId) {
    try {
      const q = query(
        collection(db, 'users', investorId, 'savedProperties'),
        orderBy('createdAt', 'desc')
      );
      const savedSnap = await getDocs(q);
      const saves = savedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Join to properties; only include ones still readable.
      const propertyResults = await Promise.allSettled(
        saves.map((s) => this.getById(s.propertyId).then((r) => r.data))
      );
      const properties = propertyResults
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);

      return { data: properties, total: properties.length };
    } catch (error) {
      console.error('Error fetching saved properties:', error);
      throw error;
    }
  },

  // Vendor view: list investors who saved a specific property
  async getSavedByProperty(propertyId) {
    try {
      const q = query(collection(db, 'properties', propertyId, 'savedBy'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const saves = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      return { data: saves, total: saves.length };
    } catch (error) {
      console.error('Error fetching property savers:', error);
      throw error;
    }
  },

  // -----------------------------
  // Shared Properties
  // Stored at:
  // - /properties/{propertyId}/sharedWith/{investorId}
  // - /users/{investorId}/sharedProperties/{propertyId}
  // -----------------------------

  _sharedDocRef(propertyId, investorId) {
    return doc(db, 'properties', propertyId, 'sharedWith', investorId);
  },

  _userSharedDocRef(investorId, propertyId) {
    return doc(db, 'users', investorId, 'sharedProperties', propertyId);
  },

  async shareToInvestors(propertyId, investorIds = [], shareMeta = {}) {
    const vendorId = auth.currentUser?.uid;
    if (!vendorId) {
      throw new Error('Must be signed in to share properties');
    }

    const uniqueInvestorIds = Array.from(
      new Set((investorIds || []).filter(Boolean).map((x) => String(x)))
    );

    if (uniqueInvestorIds.length === 0) {
      return { data: { propertyId, vendorId, sharedTo: [], sharedCount: 0 } };
    }

    // Fetch property once for rules validation and ownership check.
    const propertySnap = await getDoc(doc(db, 'properties', propertyId));
    if (!propertySnap.exists()) {
      throw new Error('Property not found');
    }

    const property = propertySnap.data();
    if (property.vendorId && property.vendorId !== vendorId) {
      throw new Error('You do not have permission to share this property');
    }

    const now = new Date();
    const batch = writeBatch(db);

    uniqueInvestorIds.forEach((investorId) => {
      const payload = {
        propertyId,
        vendorId,
        investorId,
        vendorName: shareMeta.vendorName || shareMeta.vendorDisplayName || null,
        message: shareMeta.message || null,
        createdAt: now,
      };

      batch.set(this._sharedDocRef(propertyId, investorId), payload, { merge: true });
      batch.set(this._userSharedDocRef(investorId, propertyId), payload, { merge: true });
    });

    await batch.commit();

    return { data: { propertyId, vendorId, sharedTo: uniqueInvestorIds, sharedCount: uniqueInvestorIds.length } };
  },

  async getSharedByInvestor(investorId) {
    try {
      const q = query(
        collection(db, 'users', investorId, 'sharedProperties'),
        orderBy('createdAt', 'desc')
      );
      const sharedSnap = await getDocs(q);
      const shares = sharedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Join to properties; only include ones still readable.
      const propertyResults = await Promise.allSettled(
        shares.map((s) => this.getById(s.propertyId).then((r) => r.data))
      );
      const properties = propertyResults
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);

      return { data: properties, total: properties.length };
    } catch (error) {
      console.error('Error fetching shared properties:', error);
      throw error;
    }
  },
};

// Users API
export const firestoreUsersApi = {
  // Get all users (optionally filter by userType)
  async getAll(filters = {}) {
    try {
      let q = collection(db, 'users');

      if (filters.userType) {
        q = query(q, where('userType', '==', filters.userType));
      }

      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.map((docSnap) => ({
        uid: docSnap.id,
        ...docSnap.data()
      }));

      return {
        data: users,
        total: users.length
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get user profile
  async getProfile(uid) {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('User not found');
      }

      return {
        data: {
          uid: docSnap.id,
          ...docSnap.data()
        }
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  // Update user profile
  async updateProfile(uid, updates) {
    try {
      const docRef = doc(db, 'users', uid);
      const updatedData = {
        ...updates,
        updatedAt: new Date()
      };

      await updateDoc(docRef, updatedData);

      // Fetch updated document
      const docSnap = await getDoc(docRef);
      return {
        data: {
          uid: docSnap.id,
          ...docSnap.data()
        }
      };
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }
};

// Vendors API
export const firestoreVendorsApi = {
  // Get all vendors
  async getAll() {
    try {
      const querySnapshot = await getDocs(collection(db, 'wholesalers'));
      const vendors = querySnapshot.docs.map(doc => ({
        id: doc.id,
        uid: doc.id,
        ...doc.data()
      }));

      return {
        data: vendors,
        total: vendors.length
      };
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  },

  // Get single vendor by ID
  async getById(id) {
    try {
      // Try wholesalers collection first
      let docRef = doc(db, 'wholesalers', id);
      let docSnap = await getDoc(docRef);

      // If not found, try users collection
      if (!docSnap.exists()) {
        docRef = doc(db, 'users', id);
        docSnap = await getDoc(docRef);

        // Check if user is a vendor account (legacy: 'wholesaler', current: 'vendor')
        const userType = docSnap.exists() ? (docSnap.data().userType || '').toLowerCase() : '';
        if (docSnap.exists() && (userType === 'wholesaler' || userType === 'vendor')) {
          // Try to find matching wholesaler by email
          const userData = docSnap.data();
          const q = query(
            collection(db, 'wholesalers'),
            where('email', '==', userData.email)
          );
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const wholesalerDoc = querySnapshot.docs[0];
            return {
              data: {
                id: wholesalerDoc.id,
                uid: wholesalerDoc.id,
                ...wholesalerDoc.data()
              }
            };
          }
        }
      }

      if (!docSnap.exists()) {
        throw new Error('Vendor not found');
      }

      return {
        data: {
          id: docSnap.id,
          uid: docSnap.id,
          ...docSnap.data()
        }
      };
    } catch (error) {
      console.error('Error fetching vendor:', error);
      throw error;
    }
  },

  // Get vendor stats
  async getStats(vendorId) {
    try {
      const [vendorResult, propertiesResult] = await Promise.all([
        this.getById(vendorId),
        firestorePropertiesApi.getByVendor(vendorId)
      ]);

      const vendor = vendorResult.data;
      const properties = propertiesResult.data;

      return {
        data: {
          vendor,
          propertiesCount: properties.length,
          activeListings: properties.filter(p => p.status === 'Available').length,
          totalValue: properties.reduce((sum, p) => sum + p.price, 0),
          avgPrice: properties.length > 0
            ? Math.round(properties.reduce((sum, p) => sum + p.price, 0) / properties.length)
            : 0
        }
      };
    } catch (error) {
      console.error('Error fetching vendor stats:', error);
      throw error;
    }
  },

  // Update vendor profile
  async update(id, updatedData) {
    try {
      // Since wholesalers are stored in the 'users' collection, update there
      const docRef = doc(db, 'users', id);
      const updatedDoc = {
        ...updatedData,
        updatedAt: new Date()
      };

      await updateDoc(docRef, updatedDoc);

      // Fetch updated document
      const docSnap = await getDoc(docRef);
      return {
        data: {
          id: docSnap.id,
          uid: docSnap.id,
          ...docSnap.data()
        }
      };
    } catch (error) {
      console.error('Error updating vendor:', error);
      throw error;
    }
  }
};

// Categories/Reference Data API
export const firestoreCategoriesApi = {
  // Get all categories (stored as a single document for simplicity)
  async getAll() {
    try {
      const docRef = doc(db, 'categories', 'reference');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { data: docSnap.data() };
      } else {
        // Return default categories if not found
        return {
          data: {
            propertyTypes: ['Single Family', 'Multi-Family', 'Condo', 'Townhouse', 'Commercial', 'Land'],
            locations: ['San Francisco', 'Oakland', 'San Jose', 'Palo Alto', 'Fremont', 'Livermore'],
            features: ['Hardwood Floors', 'Stainless Steel Appliances', 'City Views', 'In-Unit Laundry', 'Concierge Service']
          }
        };
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Get property types
  async getPropertyTypes() {
    try {
      const result = await this.getAll();
      return { data: result.data.propertyTypes || [] };
    } catch (error) {
      console.error('Error fetching property types:', error);
      throw error;
    }
  },

  // Get locations
  async getLocations() {
    try {
      const result = await this.getAll();
      return { data: result.data.locations || [] };
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  },

  // Get features
  async getFeatures() {
    try {
      const result = await this.getAll();
      return { data: result.data.features || [] };
    } catch (error) {
      console.error('Error fetching features:', error);
      throw error;
    }
  }
};

// Search API
export const firestoreSearchApi = {
  // Search properties
  async searchProperties(searchText, filters = {}) {
    try {
      // Firestore doesn't have full-text search, so use the public listing query
      // and apply search filters in app code to avoid composite index requirements.
      const listingResult = await firestorePropertiesApi.getAll(filters);
      let properties = listingResult.data;

      // Client-side filtering for text search (not ideal for production)
      if (searchText) {
        const searchTerm = searchText.toLowerCase();
        properties = properties.filter(p =>
          p.address?.toLowerCase().includes(searchTerm) ||
          p.description?.toLowerCase().includes(searchTerm) ||
          p.location?.toLowerCase().includes(searchTerm) ||
          p.city?.toLowerCase().includes(searchTerm)
        );
      }

      // Apply additional client-side filters
      Object.keys(filters).forEach(key => {
        if (filters[key] && !['propertyType', 'minPrice', 'maxPrice'].includes(key)) {
          properties = properties.filter(p => {
            switch (key) {
              case 'bedrooms':
                return p.bedrooms >= filters[key];
              case 'location':
                return p.city?.toLowerCase().includes(filters[key].toLowerCase());
              default:
                return true;
            }
          });
        }
      });

      return {
        data: properties,
        total: properties.length,
        query: searchText,
        filters
      };
    } catch (error) {
      console.error('Error searching properties:', error);
      throw error;
    }
  },

  // Search vendors
  async searchVendors(searchQuery) {
    try {
      const querySnapshot = await getDocs(collection(db, 'wholesalers'));
      let vendors = querySnapshot.docs.map(doc => ({
        id: doc.id,
        uid: doc.id,
        ...doc.data()
      }));

      if (searchQuery) {
        const searchTerm = searchQuery.toLowerCase();
        vendors = vendors.filter(w =>
          w.name?.toLowerCase().includes(searchTerm) ||
          w.company?.toLowerCase().includes(searchTerm) ||
          w.bio?.toLowerCase().includes(searchTerm)
        );
      }

      return {
        data: vendors,
        total: vendors.length,
        query: searchQuery
      };
    } catch (error) {
      console.error('Error searching vendors:', error);
      throw error;
    }
  }
};

// Analytics API
export const firestoreAnalyticsApi = {
  // Get dashboard stats for investors
  async getInvestorStats(userId) {
    try {
      const userResult = await firestoreUsersApi.getProfile(userId);
      const user = userResult.data;

      // Get recent properties (simplified - in production you'd track actual views)
      const propertiesResult = await firestorePropertiesApi.getAll({}, { limit: 10 });
      const viewedProperties = propertiesResult.data.slice(0, user.stats?.propertiesViewed || 10);
      const favoriteProperties = propertiesResult.data.slice(0, user.stats?.favoritesCount || 5);

      return {
        data: {
          totalViewed: user.stats?.propertiesViewed || 0,
          totalFavorites: user.stats?.favoritesCount || 0,
          offersMade: user.stats?.offersMade || 0,
          dealsClosed: user.stats?.dealsClosed || 0,
          recentViews: viewedProperties,
          favorites: favoriteProperties,
          avgPropertyPrice: viewedProperties.length > 0
            ? Math.round(viewedProperties.reduce((sum, p) => sum + p.price, 0) / viewedProperties.length)
            : 0
        }
      };
    } catch (error) {
      console.error('Error fetching investor stats:', error);
      throw error;
    }
  },

  // Get dashboard stats for vendors
  async getVendorStats(userId) {
    try {
      const [vendorResult, propertiesResult] = await Promise.all([
        firestoreVendorsApi.getById(userId),
        firestorePropertiesApi.getByVendor(userId)
      ]);

      const vendor = vendorResult.data;
      const properties = propertiesResult.data;

      return {
        data: {
          totalListings: vendor.stats?.propertiesListed || 0,
          activeListings: properties.filter(p => p.status === 'Available').length,
          totalViews: properties.reduce((sum, p) => sum + (p.views || 0), 0),
          totalValue: properties.reduce((sum, p) => sum + p.price, 0),
          avgPrice: properties.length > 0
            ? Math.round(properties.reduce((sum, p) => sum + p.price, 0) / properties.length)
            : 0,
          recentProperties: properties.slice(0, 5)
        }
      };
    } catch (error) {
      console.error('Error fetching vendor stats:', error);
      throw error;
    }
  }
};

// Landing page API
export const firestoreLandingApi = {
  // Get platform statistics (hardcoded for now, could be stored in Firestore)
  async getStats() {
    try {
      // In a real app, you'd calculate these from Firestore data
      const stats = {
        totalProperties: 1247,
        activeVendors: 89,
        totalInvestors: 2500,
        avgRating: 4.9,
        totalDealsClosed: 456,
        platformAge: 3
      };

      return { data: stats };
    } catch (error) {
      console.error('Error fetching landing stats:', error);
      throw error;
    }
  },

  // Get company contact information
  async getContactInfo() {
    try {
      const info = {
        phone: '(415) 555-0123',
        email: 'hello@offmarkets.com',
        address: 'San Francisco, CA',
        supportEmail: 'support@offmarkets.com',
        businessHours: 'Mon-Fri 9AM-6PM PST'
      };

      return { data: info };
    } catch (error) {
      console.error('Error fetching contact info:', error);
      throw error;
    }
  }
};

// Communications API - SendGrid-backed functions
export const firestoreCommsApi = {
  async sendEmail({ to, subject, text, html, from } = {}) {
    try {
      const fn = httpsCallable(functions, 'sendEmail');
      const result = await fn({ to, subject, text, html, from });
      return { data: result.data };
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  },

  // Note: implemented server-side via "email-to-SMS gateway" delivery.
  async sendText({ phone, message, carrier, gatewayDomain, subject, from } = {}) {
    try {
      const fn = httpsCallable(functions, 'sendText');
      const result = await fn({ phone, message, carrier, gatewayDomain, subject, from });
      return { data: result.data };
    } catch (error) {
      console.error('Error sending text:', error);
      throw error;
    }
  }
};

// Geocoding API - Google Maps Geocoding API
export const firestoreGeocodingApi = {
  // Geocode an address to get latitude and longitude
  async geocodeAddress(address, city, state, zipCode) {
    try {
      const fn = httpsCallable(functions, 'geocodeAddress');
      const result = await fn({ address, city, state, zipCode });
      return { data: result.data || { latitude: null, longitude: null } };
    } catch (error) {
      console.error('Error geocoding address:', error);
      return { data: { latitude: null, longitude: null } };
    }
  },

  // Reverse geocode coordinates to get address
  async reverseGeocode(latitude, longitude) {
    try {
      const fn = httpsCallable(functions, 'reverseGeocode');
      const result = await fn({ latitude, longitude });
      return { data: result.data || { address: null } };
    } catch (error) {
      console.error('Error reverse geocoding coordinates:', error);
      return { data: { address: null } };
    }
  }
};

// Storage API - Firebase Storage for file uploads
export const firestoreStorageApi = {
  // Upload a file to Firebase Storage
  async uploadFile(file, path, metadata = {}) {
    try {
      const storageRef = ref(storage, path);
      const uploadResult = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      return {
        data: {
          path: uploadResult.ref.fullPath,
          downloadURL,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
          ...metadata
        }
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  // Upload multiple files
  async uploadFiles(files, basePath) {
    try {
      const uploadPromises = files.map(async (file, index) => {
        const fileName = `${Date.now()}_${index}_${file.name}`;
        const filePath = `${basePath}/${fileName}`;
        return await this.uploadFile(file, filePath);
      });

      const results = await Promise.all(uploadPromises);
      return {
        data: results.map(result => result.data)
      };
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  },

  // Delete a file from Firebase Storage
  async deleteFile(path) {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);

      return {
        data: { path, deleted: true }
      };
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },

  // Get download URL for a file
  async getDownloadURL(path) {
    try {
      const storageRef = ref(storage, path);
      const downloadURL = await getDownloadURL(storageRef);

      return {
        data: { downloadURL }
      };
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }
};

// Export all Firestore APIs
const firestoreApi = {
  properties: firestorePropertiesApi,
  vendors: firestoreVendorsApi,
  users: firestoreUsersApi,
  categories: firestoreCategoriesApi,
  search: firestoreSearchApi,
  analytics: firestoreAnalyticsApi,
  landing: firestoreLandingApi,
  storage: firestoreStorageApi,
  geocoding: firestoreGeocodingApi
};

export default firestoreApi;
